import { useEffect, useState } from 'react';
import { recipePdfUrl, createShareLink, getShareLink, revokeShareLink } from './api';
import { recipeToText, type GetRecipeResponse, type ShareLinkView } from '@mlc/shared';
import { Banner, Button, buttonClasses, Card } from './ui';

/**
 * Partage d'une recette (US-33) : « je veux envoyer une recette réussie à
 * quelqu'un ».
 *
 * Trois formes, dans l'ordre où on s'en sert :
 *
 * 1. **Le texte**, qui est le format d'échange réel entre amis — on colle une
 *    recette dans une conversation, on ne demande pas d'installer une
 *    application. Il marche partout, y compris dans un SMS.
 * 2. **Le lien**, quand on veut que l'autre ait la vraie fiche. Il s'ouvre
 *    sans mot de passe, et se révoque : partager n'est pas publier.
 * 3. **Le PDF**, parce que c'est ce qui finit imprimé et scotché sur un
 *    placard.
 */
export function SharePanel({ detail, onClose }: { detail: GetRecipeResponse; onClose: () => void }): React.JSX.Element {
  const [link, setLink] = useState<ShareLinkView | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getShareLink(detail.id, controller.signal)
      .then(({ link: existing }) => { setLink(existing); })
      .catch(() => { /* l'absence de lien n'est pas une erreur à afficher */ });
    return () => { controller.abort(); };
  }, [detail.id]);

  function copyText(): void {
    const text = recipeToText(detail);
    void navigator.clipboard?.writeText(text).then(
      () => { setNotice('Recette copiée. Collez-la dans un message.'); },
      () => { setError('Copie impossible : sélectionnez le texte à la main.'); },
    );
  }

  function copyLink(url: string): void {
    void navigator.clipboard?.writeText(url).then(
      () => { setNotice('Lien copié.'); },
      () => { setError('Copie impossible : sélectionnez le lien à la main.'); },
    );
  }

  function create(): void {
    setBusy(true);
    setError(null);
    createShareLink(detail.id)
      .then((created) => { setLink(created); copyLink(created.url); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setBusy(false); });
  }

  function revoke(): void {
    setBusy(true);
    setError(null);
    revokeShareLink(detail.id)
      .then(() => { setLink(null); setNotice('Lien désactivé. Il ne s’ouvre plus.'); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setBusy(false); });
  }

  return (
    <Card surface aria-label="Envoyer cette recette" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-heading-2">Envoyer cette recette</h2>
        <Button type="button" variant="ghost" size="compact" onClick={onClose}>
          Fermer
        </Button>
      </div>

      {error !== null && <Banner tone="danger">{error}</Banner>}
      {notice !== null && <Banner tone="success">{notice}</Banner>}

      <div className="flex flex-col gap-2">
        <p className="text-sm text-foreground-secondary">
          Le texte se colle dans n’importe quelle messagerie, sans que l’autre ait à installer quoi que ce soit.
        </p>
        <Button type="button" variant="secondary" className="w-fit" onClick={copyText}>
          Copier la recette en texte
        </Button>
      </div>

      <div className="flex flex-col gap-2 border-t border-border pt-4">
        <p className="text-sm text-foreground-secondary">
          Un lien ouvre la vraie fiche, sans mot de passe. Il ne donne accès qu’à cette recette — ni à vos notes, ni
          à votre journal — et vous pouvez le désactiver quand vous voulez.
        </p>
        {link === null ? (
          <Button type="button" variant="secondary" className="w-fit" disabled={busy} onClick={create}>
            Créer un lien public
          </Button>
        ) : (
          <div className="flex flex-col gap-2">
            <p data-testid="share-link" className="text-data rounded-control border border-border bg-surface-raised px-3 py-2 text-xs break-all">
              {link.url}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" size="compact" onClick={() => { copyLink(link.url); }}>
                Copier le lien
              </Button>
              <Button type="button" variant="ghost" size="compact" disabled={busy} onClick={revoke}>
                Désactiver le lien
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 border-t border-border pt-4">
        <p className="text-sm text-foreground-secondary">
          Le PDF est fait pour être imprimé et scotché sur un placard.
        </p>
        <a href={recipePdfUrl(detail.id)} download className={buttonClasses('ghost', 'compact', 'w-fit')}>
          Télécharger le PDF
        </a>
      </div>
    </Card>
  );
}
